import { formatDistanceToNow } from "date-fns";
import { ArrowLeft , Info } from "lucide-react";
import { useNavigate } from "react-router-dom";

import SentryErrorBoundary from "@/components/SentryErrorBoundary";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ButtonWithShortcut } from "@/components/ui/button-with-shortcut";
import { toast } from "@/components/ui/sonner";
import BoardCreationErrorBoundary from "@/components/wizard/BoardCreationErrorBoundary";
import BoardDetailsReviewStep from "@/components/wizard/BoardDetailsReviewStep";
import BrainDumpForm from "@/components/wizard/BrainDumpForm";
import CustomBoardForm from "@/components/wizard/CustomBoardForm";
import FinalReviewStep from "@/components/wizard/FinalReviewStep";
import { GenerationProgress } from "@/components/wizard/GenerationProgress";
import PathSelector from "@/components/wizard/PathSelector";
import PersonasReviewStep from "@/components/wizard/PersonasReviewStep";
import PersonaSuggestions from "@/components/wizard/PersonaSuggestions";
import PrebuiltBoardSelector from "@/components/wizard/PrebuiltBoardSelector";
import { SaveIndicator } from "@/components/wizard/SaveIndicator";
import { useAuth } from "@/contexts/AuthContext";
import { useWizardState } from "@/hooks/useWizardState";

// Types moved to src/types/state.ts

const BoardCreationWizard = () => {
  const { user } = useAuth();
  const {
    state,
    handlePathSelection,
    handleRemix,
    handleBack,
    handleErrorReset,
    handleResumeProgress,
    handleStartFresh,
    handleBoardCreated,
    handleProblemChange,
    handleContextChange,
    handleBrainDumpChange,
    handleCustomFormSubmit,
    handleBrainDumpSubmit,
    handleBoardDetailsFeedbackSubmit,
    handlePersonaFeedbackSubmit,
    handlePersonasGenerated,
    handleUpdatePersonas,
    handleBoardSuccess,
  } = useWizardState();

  return (
    <SentryErrorBoundary
      context={{
        component: "BoardCreationWizard",
        userId: user?.id,
        additionalData: {
          currentStep: state.currentStep,
          selectedPath: state.selectedPath,
          boardType: state.selectedPath === "prebuilt" ? "prebuilt" : "custom",
        },
      }}
      onReset={handleErrorReset}
    >
      <BoardCreationErrorBoundary 
        userId={user?.id}
        boardType={state.selectedPath === "prebuilt" ? "prebuilt" : "custom"}
        onReset={handleErrorReset}
      >
        <SaveIndicator lastSaved={state.lastSaved} />
      <div className="min-h-screen bg-background">
        <header className="border-b border-border">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <ButtonWithShortcut 
              variant="ghost" 
              size="sm" 
              onClick={handleBack}
              shortcut="Esc"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </ButtonWithShortcut>
            {(state.currentStep !== "path-selection" && state.currentStep !== "prebuilt-selection") && state.lastSaved && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">
                  Draft saved {formatDistanceToNow(state.lastSaved, { addSuffix: true })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    handleStartFresh();
                    toast.success("Draft cleared - starting fresh");
                  }}
                  className="text-muted-foreground"
                >
                  Clear Draft
                </Button>
              </div>
            )}
          </div>
        </header>

        <main className="container mx-auto px-4 py-8 max-w-4xl">
          {state.isGenerating && state.generationStatus ? (
            <GenerationProgress
              currentStep={state.generationStatus.step}
              message={state.generationStatus.message}
              boardDetails={state.boardDetails}
              personas={state.fullPersonas}
            />
          ) : (
            <>
              {state.showResumePrompt && (
                <Alert className="mb-6">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm">
                        You have unsaved progress from a previous board creation. Would you like to continue where you left off?
                      </p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleStartFresh}>
                          Start Fresh
                        </Button>
                        <Button size="sm" onClick={handleResumeProgress}>
                          Resume
                        </Button>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {state.currentStep === "path-selection" && (
            <PathSelector onSelectPath={handlePathSelection} />
          )}

          {state.currentStep === "prebuilt-selection" && (
            <PrebuiltBoardSelector 
              onSuccess={handleBoardSuccess}
              onRemix={handleRemix}
            />
          )}

          {state.currentStep === "custom-form" && (
            <CustomBoardForm
              problem={state.problem}
              context={state.context}
              onProblemChange={handleProblemChange}
              onContextChange={handleContextChange}
              onSubmit={handleCustomFormSubmit}
              isSubmitting={state.isGenerating}
              remixBoard={state.remixBoard}
            />
          )}

          {state.currentStep === "braindump-form" && (
            <BrainDumpForm
              brainDump={state.brainDump}
              onBrainDumpChange={handleBrainDumpChange}
              onSubmit={handleBrainDumpSubmit}
              isSubmitting={state.isGenerating}
            />
          )}

          {state.currentStep === "review-board-details" && state.boardDetails && (
            <BoardDetailsReviewStep
              boardDetails={state.boardDetails}
              onSubmit={handleBoardDetailsFeedbackSubmit}
              isSubmitting={state.isGenerating}
            />
          )}

          {state.currentStep === "review-personas" && state.boardDetails && state.fullPersonas && (
            <PersonasReviewStep
              boardDetails={state.boardDetails}
              personas={state.fullPersonas}
              onSubmit={handlePersonaFeedbackSubmit}
              isSubmitting={state.isGenerating}
            />
          )}

          {state.currentStep === "final-review" && state.boardDetails && state.fullPersonas && (
            <FinalReviewStep
              boardDetails={state.boardDetails}
              personas={state.fullPersonas}
              onSuccess={handleBoardSuccess}
            />
          )}

          {state.currentStep === "persona-suggestions" && (
            <PersonaSuggestions
              personas={state.suggestedPersonas}
              problem={state.problem}
              context={state.context || state.brainDump}
              onUpdatePersonas={handleUpdatePersonas}
              onSuccess={handleBoardSuccess}
            />
          )}
            </>
          )}
        </main>
      </div>
      </BoardCreationErrorBoundary>
    </SentryErrorBoundary>
  );
};

export default BoardCreationWizard;
